import { Task } from '../models/types';

import Database, { SyncOperation } from './database';
import {StoreName} from "./StoreName";
import {cast} from "../utils/cast";

//@ts-ignore TS18048
class TaskStorage {
	private db: Database;

	constructor() {
		this.db = Database.getInstance();
	}

	// Получение задачи
	async getTask(taskId: string): Promise<Task | null> {
		const db = await this.db.init();

		try {
			const task = await db.get(StoreName.Tasks, taskId);
			return cast(task || null);
		} catch (error) {
			console.error('Error getting task:', error);
			return null;
		}
	}

	// Получение задач колонки
	async getTasksByColumn(columnId: string): Promise<Task[]> {
		const db = await this.db.init();

		try {
			const tasks = await db.getAllFromIndex(StoreName.Tasks, 'by-column', columnId);
			return tasks.sort(
				(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
			);
		} catch (error) {
			console.error('Error getting tasks by column:', error);
			return [];
		}
	}

	// Создание задачи
	async createTask(
		taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
	): Promise<Task> {
		const db = await this.db.init();
		const now = new Date();

		const newTask: Task = {
			...taskData,
			id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
			localId: taskData.localId || `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
			order: taskData.order || 0,
			createdAt: now,
			updatedAt: now,
			isSynced: false,
			syncVersion: 1,
		};

		try {
			await db.add(StoreName.Tasks, newTask);

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('task', newTask.id, 'create', newTask);

			return newTask;
		} catch (error) {
			console.error('Error creating task:', error);
			throw error;
		}
	}

	// Получение удаленных задач (с deletedAt)
	async getDeletedTasks(): Promise<Task[]> {
		const db = await this.db.init();

		try {
			const allTasks = await db.getAll(StoreName.Tasks);
			return allTasks.filter(task => task.deletedAt !== null);
		} catch (error) {
			console.error('Error getting deleted tasks:', error);
			return [];
		}
	}

	// Полное удаление задачи
	async permanentlyDeleteTask(taskId: string): Promise<void> {
		const db = await this.db.init();
		await db.delete(StoreName.Tasks, taskId);
	}

	async updateTask(taskId: string, updates: Partial<Task>): Promise<Task | null> {
		const db = await this.db.init();
		const transaction = db.transaction([StoreName.Tasks], 'readwrite');
		const store = transaction.objectStore(StoreName.Tasks);

		try {
			const task = await store.get(taskId);
			if (!task) {
				return null;
			}

			const updatedTask: Task = {
				...task,
				...updates,
				deletedAt: updates.deletedAt !== undefined ? updates.deletedAt : task.deletedAt,
				updatedAt: new Date(),
				syncVersion: (task.syncVersion || 1) + 1,
				isSynced: false,
			};


			await store.put(updatedTask);
			await transaction.done;

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('task', taskId, 'update', updatedTask);

			return updatedTask;
		} catch (error) {
			console.error('Error updating task:', error);
			throw error;
		}
	}

	// Обновление задачи (оптимистичное)
	async updateTaskOptimistic(
		taskId: string,
		updates: Partial<Task>
	): Promise<{
    task: Task | null;
    rollbackData: Task | null;
  }> {
		const db = await this.db.init();
		const transaction = db.transaction([StoreName.Tasks], 'readwrite');
		const store = transaction.objectStore(StoreName.Tasks);

		try {
			const task = await store.get(taskId);
			if (!task) {
				return { task: null, rollbackData: null };
			}

			// Сохраняем данные для отката
			const rollbackData = { ...task };

			const updatedTask: Task = {
				...task,
				...updates,
				deletedAt: updates.deletedAt !== undefined ? updates.deletedAt : task.deletedAt,
				order: updates.order !== undefined ? updates.order : task.order,
				updatedAt: new Date(),
				syncVersion: (task.syncVersion || 1) + 1,
				isSynced: false,
			};

			await store.put(updatedTask);
			await transaction.done;

			// Отложенное добавление в очередь синхронизации
			setTimeout(() => {
				this.addToSyncQueue('task', taskId, 'update', updatedTask).catch(console.error);
			}, 100);

			return { task: updatedTask, rollbackData };
		} catch (error) {
			console.error('Error updating task optimistically:', error);
			throw error;
		}
	}

	// Удаление задачи
	async deleteTask(taskId: string): Promise<boolean> {
		const db = await this.db.init();

		try {
			const task = await this.getTask(taskId);
			if (!task) {
				return false;
			}

			await db.delete(StoreName.Tasks, taskId);

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('task', taskId, 'delete', task);

			return true;
		} catch (error) {
			console.error('Error deleting task:', error);
			throw error;
		}
	}

	// Получение непросинхронизированных задач
	async getUnsyncedTasks(): Promise<Task[]> {
		const db = await this.db.init();

		try {
			const index = db.transaction(StoreName.Tasks).store.index('by-sync');
			const range = IDBKeyRange.bound([false], [false, new Date()]);

			let cursor = await index.openCursor(range, 'next');
			const tasks: Task[] = [];

			while (cursor) {
				tasks.push(cursor.value);
				cursor = await cursor.continue();
			}

			return tasks;
		} catch (error) {
			console.error('Error getting unsynced tasks:', error);
			return [];
		}
	}

	// Пометить задачу как синхронизированную
	async markTaskAsSynced(taskId: string, serverId: number): Promise<void> {
		const db = await this.db.init();
		const transaction = db.transaction([StoreName.Tasks], 'readwrite');
		const store = transaction.objectStore(StoreName.Tasks);

		try {
			const task = await store.get(taskId);
			if (!task) {
				return;
			}

			const updatedTask: Task = {
				...task,
				serverId,
				isSynced: true,
				updatedAt: new Date(),
			};

			await store.put(updatedTask);
			await transaction.done;
		} catch (error) {
			console.error('Error marking task as synced:', error);
			throw error;
		}
	}

	// Вспомогательный метод для добавления в очередь синхронизации
	private async addToSyncQueue(
		entityType: 'task',
		entityId: string,
		operation: 'create' | 'update' | 'delete',
		data: any
	): Promise<void> {
		const db = await this.db.init();

		const syncOp: SyncOperation = {
			id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
			entityType,
			entityId,
			operation,
			data,
			priority: operation === 'delete' ? 1 : 2,
			attempts: 0,
			maxAttempts: 3,
			createdAt: new Date(),
			updatedAt: new Date(),
		};

		await db.add(StoreName.SyncQueue, syncOp);
	}
}

export default TaskStorage;
